import React from 'react';
import { TrendingUp, Bitcoin, Building, BarChart3, PieChart, DollarSign, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const InvestmentSectors = () => {
  const sectors = [
    {
      icon: Bitcoin,
      title: 'Blockchain-based Assets',
      description: 'Navigate the digital asset landscape with expert guidance. Access innovative blockchain technologies and digital asset investments.',
      route: '/sectors/blockchain',
      color: 'from-blue-500 to-purple-600',
      bgColor: 'bg-blue-50',
      features: ['Digital Assets', 'Blockchain Tech', 'Smart Contracts']
    },
    {
      icon: Building,
      title: 'Real Estate',
      description: 'Build wealth through property investments. Access commercial, residential, and REIT opportunities for diversified exposure.',
      route: '/sectors/real-estate',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      features: ['Commercial Properties', 'REITs', 'Residential']
    },
    {
      icon: BarChart3,
      title: 'Indices',
      description: 'Diversify with index fund investments. Track major market indices with low fees and broad market exposure.',
      route: '/sectors/indices',
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-50',
      features: ['S&P 500', 'International', 'Sector ETFs']
    },
    {
      icon: PieChart,
      title: 'Stocks',
      description: 'Access global equity markets with advanced trading tools and real-time analytics for informed investment decisions.',
      route: '/sectors/stocks',
      color: 'from-red-500 to-rose-600',
      bgColor: 'bg-red-50',
      features: ['Global Markets', 'Options', 'Analysis']
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-navy-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gold-400 rounded-full opacity-20"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-navy-400 rounded-full opacity-15"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 border-2 border-gold-400 rounded-full opacity-30"></div>
      </div>
      <div className="relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
            Our Investment
            <span className="text-gradient block">Sectors</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Explore our comprehensive range of investment sectors designed to diversify 
            your portfolio and maximize growth potential across different asset classes.
          </p>
        </div>

        {/* Sectors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {sectors.map((sector, index) => {
            const IconComponent = sector.icon;
            return (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:-translate-y-1"
              >
                {/* Icon */}
                <div className={`${sector.bgColor} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="h-8 w-8 text-gray-700" />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-navy-900 mb-4 group-hover:text-gold-600 transition-colors">
                  {sector.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {sector.description}
                </p>

                {/* Features */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {sector.features.map((feature, featureIndex) => (
                      <span
                        key={featureIndex}
                        className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <Link
                  to={sector.route}
                  className="group/btn inline-flex items-center text-gold-600 font-semibold hover:text-gold-700 transition-colors"
                >
                  Explore {sector.title}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                </Link>
              </div>
            );
          })}
        </div>


        </div>
      </div>
      
      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden">
        <svg className="relative block w-full h-32" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <defs>
            <linearGradient id="sectorsWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{stopColor: '#fffbeb', stopOpacity: 1}} />
              <stop offset="25%" style={{stopColor: '#fef3c7', stopOpacity: 1}} />
              <stop offset="50%" style={{stopColor: '#fde68a', stopOpacity: 1}} />
              <stop offset="75%" style={{stopColor: '#fcd34d', stopOpacity: 1}} />
              <stop offset="100%" style={{stopColor: '#f59e0b', stopOpacity: 1}} />
            </linearGradient>
          </defs>
          <path 
            d="M0,0 C400,120 800,20 1200,80 L1200,120 L0,120 Z" 
            fill="url(#sectorsWaveGradient)"
          />
          <path 
            d="M0,40 C200,0 400,100 600,60 C800,20 1000,100 1200,50 L1200,120 L0,120 Z" 
            fill="url(#sectorsWaveGradient)"
            opacity="0.7"
          />
          <path 
            d="M0,80 C300,40 600,120 900,70 C1050,40 1150,90 1200,60 L1200,120 L0,120 Z" 
            fill="url(#sectorsWaveGradient)"
            opacity="0.5"
          />
        </svg>
      </div>
    </section>
  );
};

export default InvestmentSectors; 